// Where a visitor's campaign angle comes from, and how it stays with them.
//
// The angle arrives once, as `?a=<id>` on the ad's landing URL, and then has
// to survive everything that happens next: a client-side route change into the
// editor, a reload, a tab restore. It is read back by the hook screen (which
// copy to show, which questions to prefill) and by analytics (which ad this
// session belongs to).
//
// Two stores, deliberately:
//
//   sessionStorage   The angle for THIS tab. Scoped to the tab because that is
//                    what an ad click is: one visit from one placement. A
//                    second tab opened from a different ad gets its own, and
//                    closing the tab ends it rather than mislabelling a
//                    visitor who comes back organically next week.
//
//   PostHog super    `posthog.register` writes the angle onto EVERY event the
//   property         session sends from that moment on. This is the whole
//                    reason the funnel becomes splittable by ad without
//                    touching a single existing `track()` call: landing,
//                    onboarding steps, solve_started, upgrade_modal_opened and
//                    purchase_completed all carry `campaign_angle` for free.
//
// The `utm_*` parameters are NOT handled here. posthog-js already captures
// them off `$current_url` and persists them as session properties
// (`$entry_utm_source` and friends), so re-registering them would be a second,
// drifting copy of a thing PostHog already does correctly. `a` exists
// alongside `utm_content` rather than instead of it: `utm_content` identifies
// the CREATIVE for the ads manager, `a` selects the EXPERIENCE the product
// renders. Two ads can share an angle; one angle can be tested against two
// creatives.

import {
  CAMPAIGN_ANGLE_PARAM,
  isCampaignAngleId,
  type CampaignAngleId,
} from './angles';
import { registerSuperProperties } from '@/lib/analytics';

export { CAMPAIGN_ANGLE_PARAM };

const STORAGE_KEY = 'rt.campaign_angle';

/** Read the angle already pinned to this tab, if any. */
export function storedCampaignAngle(): CampaignAngleId | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return isCampaignAngleId(raw) ? raw : null;
  } catch {
    // Private mode, or storage disabled. The angle simply does not persist;
    // the first screen still works because it reads the URL directly.
    return null;
  }
}

/** Resolve the angle for this page load and pin it to the tab.
 *
 *  Call once, as early in the client's life as possible. Precedence:
 *
 *    1. a valid `?a=` on THIS url — a fresh click always wins, so a visitor
 *       who clicks a second ad in the same tab gets the second experience;
 *    2. whatever this tab already stored — survives the route change from
 *       /room/new into the editor, and any reload;
 *    3. null — organic. Nothing is rendered and nothing is tagged.
 *
 *  An unrecognised `?a=` value is treated as absent rather than as an error:
 *  a typo in an ad URL must degrade to the organic experience, never to a
 *  blank screen. It does NOT clear an angle already stored.
 *
 *  Returns the resolved angle so the caller can render from it immediately,
 *  without waiting for a state round trip. */
export function resolveCampaignAngle(search?: string): CampaignAngleId | null {
  if (typeof window === 'undefined') return null;

  let fromUrl: CampaignAngleId | null = null;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    const raw = params.get(CAMPAIGN_ANGLE_PARAM);
    if (isCampaignAngleId(raw)) fromUrl = raw;
  } catch {
    fromUrl = null;
  }

  const angle = fromUrl ?? storedCampaignAngle();
  if (!angle) return null;

  if (fromUrl) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // Not fatal: the angle is still live for this page, it just will not
      // survive the next navigation.
    }
  }

  // Idempotent, and cheap enough to repeat: posthog.register overwrites the
  // same key. Registering on every resolve means a reload re-applies it even
  // when the SDK started fresh.
  registerSuperProperties({ campaign_angle: angle });
  return angle;
}

/** Test seam: forget this tab's angle. Not used by application code. */
export function __clearStoredCampaignAngle(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear
  }
}
